import { AttributeType, EntityStepTypes } from '../constants/constants'
import { EntityStep } from '../redux/slices/entityInstanceSlice'
import { isValidEmail } from './EntityAttributesUtils'
import { useCallback } from 'react'
import { isEmptyValue } from './AttributeUtils'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

export const useAttributesStepValidation = (): {
    validateSteps: (data: EntityStep[]) => {
        isValidate: boolean
        validatedSteps: EntityStep[]
    }
} => {
    const { t } = useTranslation()
    const validateSteps = useCallback(
        (data: EntityStep[]) => {
            let validatedSteps = [...data]
            let isValidate = true

            validatedSteps = validatedSteps.map((s: EntityStep) => {
                if (s.stepType === EntityStepTypes.Attributes) {
                    const { isValidated, updatedFields } = validateAttributes(
                        s.attributes || [],
                    )

                    if (isValidated) {
                        const displayableFields = updatedFields.filter(
                            (i) =>
                                i.metadata?.Displayable === true &&
                                !i.hide &&
                                !i.disabled,
                        )

                        const mandatoryFields = updatedFields.filter(
                            (i) =>
                                i.required === true && !i.hide && !i.disabled,
                        )
                        // No mandatory fields
                        if (mandatoryFields.length === 0) {
                            // No mandatory & No displayableFields fields
                            if (displayableFields?.length === 0) {
                                const shownFields = updatedFields.filter(
                                    (i) => !i.hide && !i.disabled,
                                )
                                const emptyFields = shownFields.filter((i) =>
                                    isEmptyValue(i.value),
                                )

                                const isAtLeastOneFilled =
                                    emptyFields.length !== shownFields.length

                                if (!isAtLeastOneFilled) {
                                    enqueueSnackbar(
                                        t('PleaseFillAtLeastOneField'),
                                        {
                                            variant: 'error',
                                        },
                                    )
                                }

                                isValidate = isValidate && isAtLeastOneFilled
                            }
                            // if No mandatory &&  displayableFields, checking displaybleFields values
                            else {
                                const hasAtLeastOneValue =
                                    displayableFields.some(
                                        (i) => !isEmptyValue(i.value),
                                    )

                                if (!hasAtLeastOneValue) {
                                    enqueueSnackbar(
                                        t(
                                            'PleaseFillAtLeastOneDisplayableField',
                                        ),
                                        {
                                            variant: 'error',
                                        },
                                    )
                                    isValidate = false
                                }
                            }
                        }
                    } else {
                        isValidate = false
                    }

                    return {
                        ...s,
                        attributes: updatedFields,
                    }
                }

                return s
            })

            return { isValidate, validatedSteps }
        },
        [t],
    )

    return { validateSteps }
}

export const validateAttributes = (
    attributes: any[],
): {
    isValidated: boolean
    updatedFields: any[]
} => {
    let isValidated = true
    const updatedFields = attributes.map((item: any) => {
        if (
            item.required &&
            !item.hide &&
            !item.disabled &&
            (!item.value || item.value?.length === 0)
        ) {
            isValidated = false
            return {
                ...item,
                errorMessage: 'This field is required',
            }
        } else if (
            item.required &&
            !item.hide &&
            !item.disabled &&
            (item.dataType.toLowerCase() === AttributeType.MULTI_SELECT ||
                item.dataType.toLowerCase() === AttributeType.USER ||
                item.dataType.toLowerCase() === AttributeType.USER_GROUP) &&
            item.value.length <= 0
        ) {
            isValidated = false
            return {
                ...item,
                errorMessage: 'This field is required',
            }
        } else if (
            !item.hide &&
            !item.disabled &&
            item.dataType.toLowerCase() === AttributeType.EMAIL
        ) {
            if (item.value?.length > 0 && !item.metadata?.Array) {
                if (isValidEmail(item.value)) {
                    return {
                        ...item,
                        errorMessage: '',
                    }
                } else {
                    isValidated = false
                    return {
                        ...item,
                        errorMessage: 'Email is invalid',
                    }
                }
            } else if (item.required && !item.value.length) {
                isValidated = false
                return {
                    ...item,
                    errorMessage: 'This field is required',
                }
            } else {
                return {
                    ...item,
                    errorMessage: '',
                }
            }
        } else {
            return {
                ...item,
                errorMessage: '',
            }
        }
    })
    return { isValidated, updatedFields }
}
